import React, { useEffect, useMemo } from 'react';
import { CountBadge, FlexyTabs, TabStructure } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import {
    DocumentPreviewOverlay,
    ActivityTab,
    DocumentPreviewOverlayWithActions,
    DocumentSignTab,
    DossierSignDetailOverlay,
    useDossierDocumentsCtx,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules';
import { useWebsocketActions, useWebsocketState } from '@europrocurement/l2d-websocket';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DOC_TYPE_DOWNLOADABLE, DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import InformationsTab from '../InformationsTab/InformationsTab';
import { MessageTab } from '../MessageTab/MessageTab';

import FacturationTab from '../FacturationTab/FacturationTab';
import DocumentsTabs from '../DocumentsTabs/DocumentsTabs';

const DossierBody: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const { getMissingDocumentsCountByCategory } = useDossierDocumentsCtx();

    const missingDocumentsCount = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_REQUIRED,
    });
    const downloadableDocuments = getMissingDocumentsCountByCategory({
        categoryId: DOC_TYPE_DOWNLOADABLE,
    });

    const topicId = useMemo(
        () => (dossier?.id ? `messagerie_dossier_formalite_${dossier.id}` : null),
        [dossier?.id],
    );

    const { socketOpen, unreadCountByTopic } = useWebsocketState();
    const { getMessageCount } = useWebsocketActions();

    useEffect(() => {
        if (socketOpen && topicId) {
            getMessageCount(topicId);
        }
    }, [socketOpen, topicId, getMessageCount]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (topicId && socketOpen) {
                getMessageCount(topicId);
            }
        }, 50000);

        return () => {
            clearInterval(interval);
        };
    }, [topicId, socketOpen, getMessageCount]);

    const tabs: Array<TabStructure> = [
        {
            tabName: 'details',
            tabTitle: 'Détails du dossier',
            tabPanel: (
                <>
                    <InformationsTab />
                    <DocumentPreviewOverlay />
                </>
            ),
        },
        {
            tabName: 'facturation',
            tabTitle: 'Facturation du dossier',
            tabPanel: (
                <>
                    <FacturationTab
                        initialCollectionsIds={
                            dossier?.collectionId !== undefined && dossier?.collectionId !== null
                                ? [dossier.collectionId]
                                : []
                        }
                    />
                    <DocumentPreviewOverlay />
                </>
            ),
        },
        {
            tabName: 'documents',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Documents{' '}
                    {missingDocumentsCount > 0 && (
                        <Box sx={{ display: 'flex' }}>
                            <FaOptionIcon
                                sx={{
                                    lineHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                }}
                                icon={['fasl', 'file-exclamation']}
                            />
                            <CountBadge
                                count={missingDocumentsCount}
                                color="warning"
                                bgVariant="main"
                                contrastText
                            />
                        </Box>
                    )}
                    {downloadableDocuments > 0 && (
                        <Box sx={{ display: 'flex' }}>
                            <FaOptionIcon
                                sx={{
                                    lineHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                }}
                                icon={['fasl', 'file-import']}
                            />
                            <CountBadge
                                count={downloadableDocuments}
                                color="warning"
                                bgVariant="main"
                                contrastText
                            />
                        </Box>
                    )}
                </Box>
            ),
            tabPanel: (
                <>
                    <DocumentsTabs />
                    <DocumentPreviewOverlayWithActions />
                </>
            ),
        },
        {
            tabName: 'message',
            tabTitle: (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    Messages{' '}
                    <CountBadge
                        count={
                            topicId && unreadCountByTopic[topicId] ? unreadCountByTopic[topicId] : 0
                        }
                        color="primary"
                        bgVariant="light"
                    />
                </Box>
            ),
            tabPanel: (
                <>
                    <MessageTab />
                    <DocumentPreviewOverlay />
                </>
            ),
        },
        {
            tabName: 'activities',
            tabTitle: 'Activités',
            tabPanel: (
                <ActivityTab
                    showTechniqueLogs
                    showTask
                />
            ),
        },
        {
            tabName: 'signatures-electronique',
            tabTitle: 'Signature électronique',
            tabPanel: (
                <>
                    <DocumentSignTab />
                    <DossierSignDetailOverlay />
                </>
            ),
        },
    ];

    return (
        <FlexyTabs
            routerParameterName="dossier-detail"
            sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}
            tabs={tabs}
            scrollbar={false}
            indicatorColor="primary"
        />
    );
};

export default DossierBody;
